import * as React from "react";
import { CallToActionProps } from "./typings";
import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkText, UitkHeading, UitkHeadingProps } from "uitk-react-text";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { inject, observer } from "mobx-react";
import { useLocalization } from "@shared-ui/localization-context";
import { locKeys } from "./l10n/locKeys";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { withMabTracker, MabTrackEvent } from "components/utility/analytics/MabTracker";
import { CTACarouselRegionContext } from "../CTACarouselRegion/CTACarouselRegion";
import { CallToActionFlexModuleResult } from "typings/microserviceModels/call-to-action-flex-module";
import { SimpleContainerContext } from "../SimpleContainer/SimpleContainer";
import { ToggleContainerContext } from "../ToggleContainer/ToggleContainer";
import { ResponsiveToggleContainerContext } from "../ResponsiveToggleContainer/ResponsiveToggleContainer";
import { BlossomImage } from "src/components/shared/BlossomImage/BlossomImage";
import { UitkPrimaryButton } from "uitk-react-button";
import { ViewExtraLarge, ViewLarge, Viewport } from "bernie-view";
import { useLocation } from "react-router";
import { ModuleViewType } from "../Editorial/factory/EditorialContentFactory";
import { UitkSpacing } from "uitk-react-spacing";

const ENTER_BUTTON_KEY_VALUE = "Enter";
const SPACE_BUTTON_KEY_VALUE = " ";

const CardMediaImage = (props: CallToActionFlexModuleResult) => {
  const { callToAction, imageAspectRatio } = props;
  const media = callToAction?.media?.[0];
  if (!media) {
    return null;
  }

  /**
   * Optimize 16-9 image size on carousel with more than 2 CallToActions.
   * Carousel wtih more than 2 CallToActions does not require high image quality
   */
  const numberOfCarouselItems = React.useContext(CTACarouselRegionContext).carouselSize;

  // Map the appropriate crop dimension to the carousel size, defaults to size used by 6 items
  const itemsToCropDimension = (numberOfCarouselItems: number) => {
    switch (numberOfCarouselItems) {
      case 3: {
        return { width: 384, height: 216 };
      }
      case 4: {
        return { width: 285, height: 161 };
      }
      case 5: {
        return { width: 226, height: 127 };
      }
      default: {
        return { width: 186, height: 105 };
      }
    }
  };

  if (numberOfCarouselItems && numberOfCarouselItems > 2 && imageAspectRatio === "16-9") {
    return (
      <BlossomImage
        alt={media.mediaAlt}
        src={media.mediaUrl}
        imageRatio={imageAspectRatio}
        cropDimension={itemsToCropDimension(numberOfCarouselItems)}
      />
    );
  }

  return <BlossomImage alt={media.mediaAlt} src={media.mediaUrl} imageRatio={imageAspectRatio} />;
};

const uitkFigureAspectRatio = (ratio: string) => {
  switch (ratio) {
    case "1-1": {
      return UitkFigureAspectRatioType.R1_1;
    }
    case "3-2": {
      return UitkFigureAspectRatioType.R3_2;
    }
    case "3-4": {
      return UitkFigureAspectRatioType.R3_4;
    }
    case "4-3": {
      return UitkFigureAspectRatioType.R4_3;
    }
    case "21-9": {
      return UitkFigureAspectRatioType.R21_9;
    }
    default: {
      return UitkFigureAspectRatioType.R16_9;
    }
  }
};

const matchAffinityToLocKey = (callToActionName: string): string => {
  return locKeys.has(callToActionName) ? locKeys.get(callToActionName) || "" : "";
};

const returnCitiCode = (View: string): string => {
  let citiCode = "";
  if (View === ModuleViewType.Grid3) {
    const { search: searchUrl } = useLocation();
    const INITIAL_INDEX = searchUrl.indexOf("=") + 1;
    const FINAL_INDEX = searchUrl.indexOf("=") + 4;
    return searchUrl.includes("citiCode") ? searchUrl.substring(INITIAL_INDEX, FINAL_INDEX) : "";
  }

  return citiCode;
};

const getViewSettings = (
  model: CallToActionFlexModuleResult,
  moduleName: string,
  formatText: (key: string, ...args: any[]) => string
) => {
  const { excludeRfrr, callToAction, newWindowOnButtonClick } = model;
  const url = callToAction?.callToActionButton?.url ?? "";
  const editorialMargins = "all-t-margin-three m-b-margin-three l-b-margin-three xl-b-margin-three";
  const snippetMargins = "all-t-margin-three l-b-margin-three xl-b-margin-three";
  const callToActionName = model.title && model.title.replace(" ", "_");
  const rfrr = `${moduleName}.${callToActionName}.${Action.CLICK}`;
  let showEditorialContent = false;
  let cardClassName = "";
  let headingSize: UitkHeadingProps["size"] = 5;
  let figureClassName = "";
  let cardScrimType = UitkScrimType.OVERLAY;
  let callToActionLabel = "";
  let callToActionSubLabel = "";
  let callToActionTitle = model.title;
  let callToActionLink = url;
  let uitkClassNames = "all-y-gutter-three";
  let preventDefaultOnClick = false;
  let tabIndex;
  const citiCode = returnCitiCode(model.view);

  try {
    if (!excludeRfrr && model.view != ModuleViewType.Grid3) {
      const convertedUrl = new URL(url);
      convertedUrl.searchParams.set("rfrr", rfrr);
      callToActionLink = convertedUrl.toString();
    }
  } catch {
    callToActionLink = `${url}?rfrr=${rfrr}`;
  }

  switch (model.view) {
    case "affinity": {
      headingSize = 6;
      cardClassName = "cardAffinityHeading";
      figureClassName = "uitk-card-roundcorner-all";
      callToActionLabel = model.callToAction?.callToActionButton?.label;
      break;
    }
    case "theme-selector": {
      headingSize = 4;
      cardClassName = "cardAffinityHeading";
      figureClassName = "uitk-card-roundcorner-all";
      if (model.callToAction.callToActionButton.label === "l10n") {
        callToActionLabel = formatText(matchAffinityToLocKey(callToActionName.toLowerCase()));
        callToActionTitle = callToActionLabel;
      } else {
        callToActionLabel = model.callToAction?.callToActionButton?.label;
      }
      preventDefaultOnClick = true;
      tabIndex = 0;
      callToActionLink = url;
      break;
    }
    case "editorial": {
      headingSize;
      cardClassName = "uitk-type-left";
      figureClassName = "uitk-card-roundcorner-top-left uitk-card-roundcorner-top-right";
      cardScrimType = UitkScrimType.BOTTOM;
      uitkClassNames = editorialMargins;
      showEditorialContent = !!model.callToAction.text;
      callToActionLabel = model.title;
      callToActionSubLabel = model.subtitle;
      break;
    }
    case "snippet": {
      headingSize;
      cardClassName = "uitk-type-left";
      figureClassName = "uitk-card-roundcorner-top-left uitk-card-roundcorner-bottom-left";
      cardScrimType = UitkScrimType.BOTTOM;
      uitkClassNames = snippetMargins;
      showEditorialContent = !!model.callToAction.text;
      callToActionLabel = model.title;
      break;
    }
    case "full-bleed-vertical": {
      headingSize;
      cardClassName = "uitk-type-left";
      figureClassName = "uitk-card-roundcorner-all";
      cardScrimType = UitkScrimType.BOTTOM;
      uitkClassNames = editorialMargins;
      showEditorialContent = !!model.callToAction.text;
      callToActionLabel = model.title;
      callToActionSubLabel = model.subtitle;
      break;
    }
    case ModuleViewType.Grid3: {
      uitkClassNames = editorialMargins;
      showEditorialContent = !!model.callToAction.text;
      callToActionLabel = model.title;
      callToActionSubLabel = model.subtitle;
      callToActionLink = citiCode ? `${callToActionLink}&citiCode=${citiCode}` : callToActionLink;
      break;
    }
  }

  return {
    headingSize,
    cardClassName,
    figureClassName,
    cardScrimType,
    callToActionLabel,
    callToActionSubLabel,
    callToActionTitle,
    callToActionLink,
    uitkClassNames,
    showEditorialContent,
    preventDefaultOnClick,
    tabIndex,
    newWindowOnButtonClick,
  };
};

export const CallToAction = withMabTracker(
  inject(
    "analytics",
    "compositionStore",
    "themeSelectorStore",
    "context",
    "flexModuleModelStore"
  )(
    observer((props: CallToActionProps) => {
      const { flexTemplateNode, flexModuleModelStore, compositionStore, analytics, themeSelectorStore } = props;
      const { id, options } = flexTemplateNode;
      const model = flexModuleModelStore.getModel(id) as CallToActionFlexModuleResult | null;

      if (options.ajaxOnly || !model) {
        return null;
      }

      const { formatText } = useLocalization();
      const simpleContainerContext = React.useContext(SimpleContainerContext);
      const toggleContainerContext = React.useContext(ToggleContainerContext);
      const responsiveToggleContainerContext = React.useContext(ResponsiveToggleContainerContext);

      const ctaCarouselContext = React.useContext(CTACarouselRegionContext);

      const shouldRenderBorder =
        simpleContainerContext.modulesHaveBorder ||
        toggleContainerContext.modulesHaveBorder ||
        responsiveToggleContainerContext.modulesHaveBorder ||
        ctaCarouselContext.hasBorder;
      const viewProps = getViewSettings(model, options.flexComponentName, formatText);

      const keyboardPress = (e: React.KeyboardEvent) => {
        //button elements should allow click on enter or space when a keydown event occurs. Allow by default when a mouse event.
        const allowClick = e.key === ENTER_BUTTON_KEY_VALUE || e.key === SPACE_BUTTON_KEY_VALUE;

        if (allowClick) {
          cardClicked(e);
        }
      };

      const cardClicked = (e: React.SyntheticEvent) => {
        const rfrr = `nativeMarqueeTile.tile.click`;

        if (compositionStore && model.nextScene) {
          themeSelectorStore?.updateActiveTheme(model.affinity);
          compositionStore.changeScene(model.nextScene);
        }

        const trackingInfo: FlexTrackingInfo = {
          moduleName: options.flexComponentName,
          rfrr,
          action: Action.CLICK,
        };

        if (props.mabTrack) {
          const mabTrackData: MabTrackEvent = {
            moduleName: "call-to-action",
            eventTarget: "call-to-action",
            eventType: "homepageClick.nonPath",
            rfrr,
          };
          props.mabTrack(mabTrackData);
        }

        sendDelayedTrackEvent(trackingInfo, analytics);

        if (viewProps.preventDefaultOnClick) {
          e.preventDefault();
        }
      };

      return (
        <div
          id={id}
          data-fm={options.fmId}
          data-fm-title-id={options.fmTitleId}
          className={`uitk-cell uitk-type-center CallToAction all-cell-1-1 ${viewProps.uitkClassNames}`}
          onClick={cardClicked}
          onKeyDown={keyboardPress}
          tabIndex={viewProps.tabIndex}
        >
          <UitkCard overflow border={shouldRenderBorder}>
            {model.view === "snippet" ? (
              <UitkLayoutGrid columns={4}>
                <>
                  <UitkLayoutGridItem colSpan={1}>
                    <UitkFigure
                      ratio={uitkFigureAspectRatio(model.imageAspectRatio)}
                      className={viewProps.figureClassName}
                    >
                      <CardMediaImage {...model} />
                      <UitkScrim type={viewProps.cardScrimType}>
                        <UitkHeading className={viewProps.cardClassName} size={viewProps.headingSize}>
                          {viewProps.callToActionLabel}
                        </UitkHeading>
                        <UitkText align="left" size={300}>
                          {viewProps.callToActionSubLabel}
                        </UitkText>
                      </UitkScrim>
                    </UitkFigure>
                  </UitkLayoutGridItem>
                  <UitkLayoutGridItem colSpan={3}>
                    {viewProps.showEditorialContent && (
                      <UitkCardContentSection>
                        <UitkText size={200}>
                          <div
                            className="uitk-type-left"
                            dangerouslySetInnerHTML={{ __html: model.callToAction.text }}
                          />
                        </UitkText>
                      </UitkCardContentSection>
                    )}
                  </UitkLayoutGridItem>
                </>
              </UitkLayoutGrid>
            ) : model.view === ModuleViewType.Grid3 ? (
              <>
                <UitkSpacing padding={{ large: { blockend: "six" }, extra_large: { blockend: "six" } }}>
                  <Viewport>
                    <ViewExtraLarge>
                      <UitkPrimaryButton type="button" href={viewProps.callToActionLink}>
                        Apply Now
                      </UitkPrimaryButton>
                    </ViewExtraLarge>
                    <ViewLarge>
                      <UitkPrimaryButton type="button" href={viewProps.callToActionLink}>
                        Apply Now
                      </UitkPrimaryButton>
                    </ViewLarge>
                  </Viewport>
                </UitkSpacing>
              </>
            ) : (
              <>
                <UitkFigure ratio={uitkFigureAspectRatio(model.imageAspectRatio)} className={viewProps.figureClassName}>
                  <CardMediaImage {...model} />
                  <UitkScrim type={viewProps.cardScrimType}>
                    <UitkHeading className={viewProps.cardClassName} size={viewProps.headingSize}>
                      {viewProps.callToActionLabel}
                    </UitkHeading>
                    <UitkText align="left" size={300}>
                      {viewProps.callToActionSubLabel}
                    </UitkText>
                  </UitkScrim>
                </UitkFigure>
                {viewProps.showEditorialContent && (
                  <UitkCardContentSection>
                    <UitkText size={200}>
                      <div className="uitk-type-left" dangerouslySetInnerHTML={{ __html: model.callToAction.text }} />
                    </UitkText>
                  </UitkCardContentSection>
                )}
              </>
            )}
            {!model.nextScene && (
              <UitkCardLink>
                <a
                  data-testid="call-to-action-link"
                  href={viewProps.callToActionLink}
                  target={viewProps.newWindowOnButtonClick ? "_blank" : "_self"}
                  rel={viewProps.newWindowOnButtonClick ? "noopener noreferrer" : undefined}
                >
                  {viewProps.callToActionTitle}
                  {viewProps.newWindowOnButtonClick && (
                    <UitkIcon name="open_in_new" size={UitkIconSize.SMALL} title={formatText("opens.in.new.window")} />
                  )}
                </a>
              </UitkCardLink>
            )}
          </UitkCard>
        </div>
      );
    })
  )
);

export default CallToAction;
